// The facility. ~100 m² (10×10), four scaled rooms + a reception/recibidor, top-down:
//
//   x=0                          x=5                          x=10
//  z=0 +----------[ window ]------+----------------------------+
//      |        DRY SAUNA          |        STEAM SAUNA         |
//      |   (all cedar, porthole,   |   (fogged GLASS door,      |
//      |    WOOD door w/ window)   |    the scald)              |
//  z=4 +--[wood door]----+---------+------[glass door]----------+
//      |                                                         |
//      |            REST / COOLING AREA                          |
//      |   (cold showers · benches · cold PLUNGE pool · drain)   |
//  z=7 +-------------------[ open archway ]----------------------+
//      |              RECEPTION  →  [ EXIT ]                      |
// z=10 +----------------------+----------------------------------+
//
// Player spawns in the REST AREA facing the saunas (north / -z). The build
// returns geometry plus the handles the rest of the game wires interaction to.

import THREE from "../three.js";
import type { Object3D, Group, Vector3, Light, Mesh } from "three";

export type RoomId = "reception" | "rest" | "dry_sauna" | "steam_sauna" | "outside";

export interface AABB {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export interface Seat {
  id: string;
  pos: Vector3; // seated eye anchor (we lower the camera here)
  yaw: number; // facing when seated
  room: RoomId;
}

export interface DoorHandle {
  name: string;
  group: Group;
  isOpen: boolean;
  toggle: () => void;
}

export interface World {
  root: Group;
  colliders: AABB[];
  seats: Seat[];
  doors: Record<string, DoorHandle>;
  spawn: { pos: Vector3; yaw: number };
  exitZone: AABB;
  /** Empty object positioned just outside the dry-sauna window — where the face mounts. */
  windowAnchor: Object3D;
  lights: Light[];
  flickerLights: Light[];
  shower: { water: Object3D; valve: Object3D; isOn: boolean; controlPos: Vector3; setOn: (b: boolean) => void };
  roomAt: (x: number, z: number) => RoomId;
}

const H = 2.6; // wall height (a touch taller for the larger rooms)
const T = 0.12; // wall thickness
const FP = 10; // footprint (square, metres)

// ── Room bounds (single source of truth; spectator clamps to these too) ──
export const ROOMS: Record<Exclude<RoomId, "outside">, AABB> = {
  dry_sauna: { minX: 0, maxX: 5, minZ: 0, maxZ: 4 },
  steam_sauna: { minX: 5, maxX: 10, minZ: 0, maxZ: 4 },
  rest: { minX: 0, maxX: 10, minZ: 4, maxZ: 7 },
  reception: { minX: 0, maxX: 10, minZ: 7, maxZ: 10 },
};

// ── Procedural textures (canvas → CanvasTexture). No external assets. ──
function noiseTex(
  base: [number, number, number],
  opts: { kind: "concrete" | "wood" | "tile"; noise: number; repeat: [number, number] },
): THREE.Texture {
  const S = 256;
  const c = document.createElement("canvas");
  c.width = c.height = S;
  const g = c.getContext("2d")!;
  g.fillStyle = `rgb(${base[0]},${base[1]},${base[2]})`;
  g.fillRect(0, 0, S, S);
  // grain
  const img = g.getImageData(0, 0, S, S);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() * 2 - 1) * opts.noise;
    d[i] = clamp(d[i] + n);
    d[i + 1] = clamp(d[i + 1] + n);
    d[i + 2] = clamp(d[i + 2] + n);
  }
  g.putImageData(img, 0, 0);
  if (opts.kind === "wood") {
    g.strokeStyle = "rgba(0,0,0,0.22)";
    for (let y = 10; y < S; y += 26) { g.beginPath(); g.moveTo(0, y); g.lineTo(S, y); g.stroke(); }
    g.strokeStyle = "rgba(255,220,160,0.05)";
    for (let k = 0; k < 40; k++) {
      g.beginPath();
      const y = Math.random() * S;
      g.moveTo(0, y);
      g.bezierCurveTo(S / 3, y + (Math.random() * 8 - 4), (2 * S) / 3, y + (Math.random() * 8 - 4), S, y);
      g.stroke();
    }
  } else if (opts.kind === "tile") {
    g.strokeStyle = "rgba(0,0,0,0.35)";
    g.lineWidth = 2;
    for (let p = 0; p <= S; p += 64) {
      g.beginPath(); g.moveTo(p, 0); g.lineTo(p, S); g.stroke();
      g.beginPath(); g.moveTo(0, p); g.lineTo(S, p); g.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(opts.repeat[0], opts.repeat[1]);
  return tex;
}
function clamp(v: number): number { return v < 0 ? 0 : v > 255 ? 255 : v; }

const wallTex = () => noiseTex([140, 130, 115], { kind: "concrete", noise: 22, repeat: [5, 2] });
const woodTex = () => noiseTex([148, 96, 51], { kind: "wood", noise: 18, repeat: [4, 3] });
const tileTex = () => noiseTex([89, 92, 100], { kind: "tile", noise: 12, repeat: [6, 6] });
const floorTex = () => noiseTex([74, 68, 60], { kind: "concrete", noise: 26, repeat: [10, 10] });

const MAT = {
  wall: new THREE.MeshStandardMaterial({ map: wallTex(), bumpMap: wallTex(), bumpScale: 0.04, color: 0xffffff, roughness: 0.95 }),
  woodHot: new THREE.MeshStandardMaterial({ map: woodTex(), bumpMap: woodTex(), bumpScale: 0.05, color: 0xffffff, roughness: 0.8 }),
  tile: new THREE.MeshStandardMaterial({ map: tileTex(), bumpMap: tileTex(), bumpScale: 0.03, color: 0xffffff, roughness: 0.55, metalness: 0.05 }),
  floor: new THREE.MeshStandardMaterial({ map: floorTex(), bumpMap: floorTex(), bumpScale: 0.06, color: 0xffffff, roughness: 0.9 }),
  ceiling: new THREE.MeshStandardMaterial({ color: 0x2a2722, roughness: 1 }),
  door: new THREE.MeshStandardMaterial({ color: 0x4a3620, roughness: 0.7 }),
  metal: new THREE.MeshStandardMaterial({ color: 0x9a9aa0, roughness: 0.35, metalness: 0.6 }),
  glass: new THREE.MeshStandardMaterial({
    color: 0x9fb0b5,
    roughness: 0.1,
    metalness: 0.1,
    transparent: true,
    opacity: 0.28,
  }),
};

// ── Prop materials (decorative only) ──────────────────────────────────────────
const PROP = {
  // dark frame / jamb wood, near-black
  darkWood: new THREE.MeshStandardMaterial({ color: 0x231811, roughness: 0.8 }),
  // dark metal grate / fixtures
  grate: new THREE.MeshStandardMaterial({ color: 0x1c1d20, roughness: 0.5, metalness: 0.7 }),
  // sooty stove rock
  rock: new THREE.MeshStandardMaterial({ color: 0x2b2825, roughness: 1 }),
  // glowing löyly rock (faint warm emissive)
  rockHot: new THREE.MeshStandardMaterial({ color: 0x3a2a20, emissive: 0xff4a12, emissiveIntensity: 0.6, roughness: 0.9 }),
  // cool steam-generator vent glow
  ventCool: new THREE.MeshStandardMaterial({ color: 0x223036, emissive: 0x3f8fb0, emissiveIntensity: 0.5, roughness: 0.6 }),
  // pale ceramic / bucket pail (reuse-ish of metal but lighter)
  pail: new THREE.MeshStandardMaterial({ color: 0x6b4a2c, roughness: 0.85 }),
  // off-white towel / paper
  cloth: new THREE.MeshStandardMaterial({ color: 0xd8d2c4, roughness: 1 }),
  // warm emissive lamp shade
  lampWarm: new THREE.MeshStandardMaterial({ color: 0xffe0a0, emissive: 0xffb866, emissiveIntensity: 0.9, roughness: 0.7 }),
  // emissive EXIT sign (amber)
  exitSign: new THREE.MeshBasicMaterial({ color: 0xffae33 }),
  // ceiling lamp housing
  housing: new THREE.MeshStandardMaterial({ color: 0x16140f, roughness: 0.9, metalness: 0.2 }),
  // cold plunge water (faint cool emissive so it reads in the dark)
  plunge: new THREE.MeshStandardMaterial({ color: 0x2b4d63, emissive: 0x1d3f55, emissiveIntensity: 0.35, transparent: true, opacity: 0.78, roughness: 0.15, metalness: 0.2 }),
};

// Pale board with text drawn onto a CanvasTexture — used for the rule placard.
function signTex(lines: string[], headerIdx = 0): THREE.Texture {
  const W = 256;
  const Hpx = 192;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = Hpx;
  const g = c.getContext("2d")!;
  // aged-paper background with a faint grain
  g.fillStyle = "#cdc4ac";
  g.fillRect(0, 0, W, Hpx);
  const img = g.getImageData(0, 0, W, Hpx);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() * 2 - 1) * 10;
    d[i] = clamp(d[i] + n);
    d[i + 1] = clamp(d[i + 1] + n);
    d[i + 2] = clamp(d[i + 2] + n);
  }
  g.putImageData(img, 0, 0);
  g.fillStyle = "#1a140d";
  g.textAlign = "center";
  const step = Hpx / (lines.length + 1);
  lines.forEach((ln, i) => {
    g.font = `${i === headerIdx ? "bold 26" : "22"}px monospace`;
    g.fillText(ln, W / 2, step * (i + 1) + 8);
  });
  const tex = new THREE.CanvasTexture(c);
  return tex;
}

export function buildWorld(): World {
  const root = new THREE.Group();
  const colliders: AABB[] = [];
  const lights: Light[] = [];
  const flickerLights: Light[] = [];

  // ── Floor & ceiling (whole 10×10 footprint) ──
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(FP, FP), MAT.floor);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(FP / 2, 0, FP / 2);
  root.add(floor);

  const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(FP, FP), MAT.ceiling);
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.set(FP / 2, H, FP / 2);
  root.add(ceiling);

  // tile/wood patches so floors read differently per zone
  addFloorPatch(root, 0, 4, 10, 7, MAT.tile); // rest
  addFloorPatch(root, 0, 7, 10, 10, MAT.tile); // reception
  addFloorPatch(root, 0, 0, 5, 4, MAT.woodHot); // dry sauna floorboards
  addFloorPatch(root, 5, 0, 10, 4, MAT.woodHot); // steam sauna

  // ── Wall helper ──
  const wall = (x1: number, z1: number, x2: number, z2: number, mat = MAT.wall) => {
    addWall(root, colliders, x1, z1, x2, z2, mat);
  };

  // Outer perimeter
  // north (z=0): dry-sauna window gap x[2..3]
  wall(0, 0, 2, 0);
  wall(3, 0, 10, 0);
  // south (z=10): exit gap x[4.5..5.5]
  wall(0, 10, 4.5, 10);
  wall(5.5, 10, 10, 10);
  // west / east
  wall(0, 0, 0, 10);
  wall(10, 0, 10, 10);

  // Dry-sauna porthole window in the north wall gap (x 2..3)
  buildWindow(root, colliders);
  const windowAnchor = new THREE.Object3D();
  windowAnchor.position.set(2.5, 1.35, -0.25); // just OUTSIDE the glass
  root.add(windowAnchor);

  // Internal wall: saunas | rest, at z=4 — gaps for the two sauna doors
  wall(0, 4, 1, 4); // left of dry door
  wall(2, 4, 7, 4); // between dry door and steam door (crosses the x=5 divider)
  wall(8, 4, 10, 4); // right of steam door
  // Divider between the two saunas
  wall(5, 0, 5, 4);
  // Internal wall: rest | reception, at z=7 — open archway gap x 4..6
  wall(0, 7, 4, 7);
  wall(6, 7, 10, 7);

  // ── Doors ──
  // Dry: all wood with a central window (see out into the rest area).
  // Steam: full glass (see straight through). Exit: metal.
  const doors: Record<string, DoorHandle> = {};
  doors.dry = makeDoor(root, colliders, "dry", 1, 4, 1, "wood");
  doors.steam = makeDoor(root, colliders, "steam", 7, 4, 1, "glass");
  doors.exit = makeDoor(root, colliders, "exit", 4.5, 10, 1, "metal");

  // ── Benches & seats ──
  // Sauna benches sit along a wall AWAY from the doorway so they never wall you
  // off from the door. Seats face the room's focal point (the window for dry).
  const seats: Seat[] = [];

  // Dry sauna: L-bench along the SOUTH wall right of the door (gap x1..2) and up
  // the EAST wall. Seated bathers face -z toward the porthole.
  addBench(root, 2.4, 3.3, 4.7, 3.9, MAT.woodHot); // south run
  colliders.push({ minX: 2.4, maxX: 4.7, minZ: 3.3, maxZ: 3.9 });
  addBench(root, 4.3, 0.5, 4.9, 3.3, MAT.woodHot); // east run
  colliders.push({ minX: 4.3, maxX: 4.9, minZ: 0.5, maxZ: 3.3 });
  tieredBench(root, 2.4, 4.7, 4); // upper tier + backrest against the z=4 wall
  [2.9, 3.5, 4.0].forEach((x, i) =>
    seats.push({ id: `dry_${i}`, pos: new THREE.Vector3(x, 1.15, 3.5), yaw: 0, room: "dry_sauna" }),
  );

  // Steam sauna: mirror — L-bench along the SOUTH wall left of the door (gap
  // x7..8) and up the EAST wall.
  addBench(root, 5.3, 3.3, 6.9, 3.9, MAT.woodHot); // south run
  colliders.push({ minX: 5.3, maxX: 6.9, minZ: 3.3, maxZ: 3.9 });
  addBench(root, 9.1, 0.5, 9.7, 3.9, MAT.woodHot); // east run
  colliders.push({ minX: 9.1, maxX: 9.7, minZ: 0.5, maxZ: 3.9 });
  tieredBench(root, 5.3, 6.9, 4);
  [5.7, 6.3, 6.7].forEach((x, i) =>
    seats.push({ id: `steam_${i}`, pos: new THREE.Vector3(x, 1.15, 3.5), yaw: 0, room: "steam_sauna" }),
  );

  // Rest / cooling area: a long cooling bench against the z=4 wall (between the
  // two sauna doors), facing +z into the room (away from the saunas).
  addBench(root, 2.4, 4.12, 6.6, 4.62, MAT.tile);
  colliders.push({ minX: 2.4, maxX: 6.6, minZ: 4.12, maxZ: 4.62 });
  [3.1, 4.0, 4.9, 5.8].forEach((x, i) =>
    seats.push({ id: `rest_${i}`, pos: new THREE.Vector3(x, 1.05, 4.9), yaw: Math.PI, room: "rest" }),
  );

  // ── Cold showers (west wall of the rest area) ──
  const shower = buildShowers(root);

  // ── Cold plunge pool (east side of the rest area) ──
  buildPlunge(root, colliders, 7.0, 4.6, 9.4, 6.4);

  // ── Reception desk ──
  addBench(root, 0.4, 7.4, 3.0, 8.0, MAT.wall, 1.05);
  colliders.push({ minX: 0.4, maxX: 3.0, minZ: 7.4, maxZ: 8.0 });

  // ── Sauna stove in the dry sauna (warm glow), NE corner ──
  const stove = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.7, 0.5), MAT.metal);
  stove.position.set(0.5, 0.35, 0.5);
  root.add(stove);
  colliders.push({ minX: 0.2, maxX: 0.8, minZ: 0.2, maxZ: 0.8 });
  const coals = new THREE.PointLight(0xff5a1e, 1.6, 5, 2);
  coals.position.set(0.5, 0.75, 0.5);
  root.add(coals);
  lights.push(coals);
  flickerLights.push(coals);

  // ── Lighting ──
  // Hemisphere fill keeps the place readable; point lamps give each room mood.
  const hemi = new THREE.HemisphereLight(0x9a9080, 0x2a2620, 0.9);
  root.add(hemi);
  lights.push(hemi);
  const ambient = new THREE.AmbientLight(0x55504a, 0.55);
  root.add(ambient);
  lights.push(ambient);

  const lamp = (x: number, z: number, color: number, intensity: number) => {
    const l = new THREE.PointLight(color, intensity, 11, 1.0);
    l.position.set(x, H - 0.25, z);
    root.add(l);
    lights.push(l);
    return l;
  };
  flickerLights.push(lamp(2.5, 1.8, 0xffb066, 2.2)); // dry sauna (warm)
  flickerLights.push(lamp(7.5, 1.8, 0xaebec2, 1.35)); // steam (cold, dim)
  lamp(3, 5.5, 0xd9d2c2, 2.2); // rest (west)
  lamp(7, 5.5, 0xc6cfd4, 1.9); // rest (east, over the plunge)
  lamp(3, 8.5, 0xbfd0d8, 2.0); // reception (west)
  lamp(7, 8.5, 0xbfd0d8, 1.8); // reception (east)

  // Steam fog volume hint in the steam sauna
  buildSteam(root, 7.5, 1.8);

  // Decorative scenography (signs, drain, rocks, benches, reception details…).
  buildProps(root, lights);

  return {
    root,
    colliders,
    seats,
    doors,
    spawn: { pos: new THREE.Vector3(5, 1.6, 5.5), yaw: 0 }, // rest area, facing the saunas
    exitZone: { minX: 4.5, maxX: 5.5, minZ: 10.0, maxZ: 11.5 },
    windowAnchor,
    lights,
    flickerLights,
    shower,
    roomAt,
  };
}

// ── Room lookup ──
function roomAt(x: number, z: number): RoomId {
  if (z < 4) return x < 5 ? "dry_sauna" : "steam_sauna";
  if (z < 7) return "rest";
  if (z <= 10.05) return "reception";
  return "outside";
}

// ── Geometry helpers ──────────────────────────────────────────────────────────

function addFloorPatch(root: Group, x1: number, z1: number, x2: number, z2: number, mat: THREE.Material) {
  const g = new THREE.Mesh(new THREE.PlaneGeometry(x2 - x1, z2 - z1), mat);
  g.rotation.x = -Math.PI / 2;
  g.position.set((x1 + x2) / 2, 0.01, (z1 + z2) / 2);
  root.add(g);
}

function addWall(root: Group, colliders: AABB[], x1: number, z1: number, x2: number, z2: number, mat: THREE.Material) {
  const horizontal = z1 === z2;
  const len = horizontal ? Math.abs(x2 - x1) : Math.abs(z2 - z1);
  if (len < 1e-3) return;
  const w = horizontal ? len : T;
  const d = horizontal ? T : len;
  const geo = new THREE.BoxGeometry(w, H, d);
  const mesh = new THREE.Mesh(geo, mat);
  const cx = (x1 + x2) / 2;
  const cz = (z1 + z2) / 2;
  mesh.position.set(cx, H / 2, cz);
  root.add(mesh);
  colliders.push({
    minX: cx - w / 2,
    maxX: cx + w / 2,
    minZ: cz - d / 2,
    maxZ: cz + d / 2,
  });
}

function addBench(root: Group, x1: number, z1: number, x2: number, z2: number, mat: THREE.Material, h = 0.45) {
  const w = x2 - x1;
  const d = z2 - z1;
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set((x1 + x2) / 2, h / 2, (z1 + z2) / 2);
  root.add(mesh);
}

/** Porthole window in the dry-sauna north wall (gap x2..3). Pillars + sill +
 * transom + a transparent glass pane; the frame is thin borders so whatever
 * presses against it from outside stays visible. */
function buildWindow(root: Group, colliders: AABB[]) {
  const cx = 2.5;
  const sill = new THREE.Mesh(new THREE.BoxGeometry(1.0, 1.0, T), MAT.wall);
  sill.position.set(cx, 0.5, 0);
  root.add(sill);
  const transom = new THREE.Mesh(new THREE.BoxGeometry(1.0, H - 1.6, T), MAT.wall);
  transom.position.set(cx, H - (H - 1.6) / 2, 0);
  root.add(transom);
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x2a1d10 });
  const borders: [number, number, number, number][] = [
    [0.9, 0.06, cx, 1.61], // top
    [0.9, 0.06, cx, 1.09], // bottom
    [0.06, 0.56, cx - 0.42, 1.35], // left
    [0.06, 0.56, cx + 0.42, 1.35], // right
  ];
  for (const [w, h, x, y] of borders) {
    const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.07), frameMat);
    b.position.set(x, y, 0);
    root.add(b);
  }
  const glass = new THREE.Mesh(new THREE.BoxGeometry(0.84, 0.5, 0.04), MAT.glass);
  glass.position.set(cx, 1.35, 0);
  root.add(glass);
  colliders.push({ minX: 2, maxX: 3, minZ: -T / 2, maxZ: T / 2 });
}

type DoorStyle = "wood" | "glass" | "metal";

/** A hinged door at a wall gap that runs along X. `style` picks the leaf:
 *  - "wood"  : solid wood with a central glass window (dry sauna)
 *  - "glass" : a full transparent pane in a thin frame (steam sauna)
 *  - "metal" : a solid metal leaf (exit) */
function makeDoor(
  root: Group,
  colliders: AABB[],
  name: string,
  hingeX: number,
  z: number,
  width: number,
  style: DoorStyle,
): DoorHandle {
  const group = new THREE.Group();
  group.position.set(hingeX, 0, z);
  const Hd = 2.1;
  const W = width;

  if (style === "metal") {
    const panel = new THREE.Mesh(new THREE.BoxGeometry(W, Hd, 0.06), MAT.metal);
    panel.position.set(W / 2, Hd / 2, 0);
    group.add(panel);
  } else if (style === "glass") {
    const pane = new THREE.Mesh(new THREE.BoxGeometry(W - 0.08, Hd - 0.08, 0.04), MAT.glass);
    pane.position.set(W / 2, Hd / 2, 0);
    group.add(pane);
    // thin wood frame: two stiles + two rails
    for (const sx of [0.04, W - 0.04]) {
      const stile = new THREE.Mesh(new THREE.BoxGeometry(0.08, Hd, 0.06), MAT.door);
      stile.position.set(sx, Hd / 2, 0);
      group.add(stile);
    }
    for (const sy of [0.05, Hd - 0.05]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(W, 0.1, 0.06), MAT.door);
      rail.position.set(W / 2, sy, 0);
      group.add(rail);
    }
  } else {
    // wood with a central window: bottom panel + top panel + two stiles, leaving
    // a glazed opening in the middle.
    const stileW = 0.16;
    const botTop = Hd * 0.42; // top of the bottom solid panel
    const topBot = Hd * 0.82; // bottom of the top solid panel
    const bottom = new THREE.Mesh(new THREE.BoxGeometry(W, botTop, 0.06), MAT.door);
    bottom.position.set(W / 2, botTop / 2, 0);
    group.add(bottom);
    const top = new THREE.Mesh(new THREE.BoxGeometry(W, Hd - topBot, 0.06), MAT.door);
    top.position.set(W / 2, (Hd + topBot) / 2, 0);
    group.add(top);
    for (const sx of [stileW / 2, W - stileW / 2]) {
      const stile = new THREE.Mesh(new THREE.BoxGeometry(stileW, topBot - botTop, 0.06), MAT.door);
      stile.position.set(sx, (botTop + topBot) / 2, 0);
      group.add(stile);
    }
    const pane = new THREE.Mesh(new THREE.BoxGeometry(W - 2 * stileW, topBot - botTop, 0.03), MAT.glass);
    pane.position.set(W / 2, (botTop + topBot) / 2, 0);
    group.add(pane);
  }

  const handle = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 8), MAT.metal);
  handle.position.set(W - 0.12, 1.05, 0.06);
  group.add(handle);
  root.add(group);

  const closedCollider: AABB = {
    minX: hingeX,
    maxX: hingeX + W,
    minZ: z - 0.08,
    maxZ: z + 0.08,
  };
  colliders.push(closedCollider);

  const handleObj: DoorHandle = {
    name,
    group,
    isOpen: false,
    toggle() {
      this.isOpen = !this.isOpen;
      group.rotation.y = this.isOpen ? -Math.PI / 2 : 0;
      const idx = colliders.indexOf(closedCollider);
      if (this.isOpen && idx >= 0) colliders.splice(idx, 1);
      if (!this.isOpen && idx < 0) colliders.push(closedCollider);
    },
  };
  return handleObj;
}

/** A row of three cold showers down the rest-area west wall. The middle one is
 * the interactive shower (its water toggles); the others are decorative. */
function buildShowers(root: Group) {
  const head = (z: number) => {
    const h = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.06, 12), MAT.metal);
    h.position.set(0.28, 2.1, z);
    root.add(h);
    const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.5, 8), MAT.metal);
    pipe.position.set(0.14, 2.2, z);
    pipe.rotation.z = Math.PI / 2;
    root.add(pipe);
    const valve = new THREE.Mesh(new THREE.SphereGeometry(0.06, 10, 10), MAT.metal);
    valve.position.set(0.22, 1.3, z);
    root.add(valve);
    return valve;
  };
  head(4.8);
  const valve = head(5.5);
  head(6.2);

  const water = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.12, 2.0, 10, 1, true),
    new THREE.MeshStandardMaterial({ color: 0xaecadb, transparent: true, opacity: 0.35, roughness: 0.2 }),
  );
  water.position.set(0.28, 1.1, 5.5);
  water.visible = false;
  root.add(water);

  return {
    water,
    valve,
    isOn: false,
    controlPos: new THREE.Vector3(0.5, 1.3, 5.5),
    setOn(b: boolean) {
      this.isOn = b;
      water.visible = b;
    },
  };
}

/** Sunken cold plunge pool: tiled coping rim, dark recessed walls, and a faint
 * cool water plane. Solid collider — you walk around it, you don't fall in. */
function buildPlunge(root: Group, colliders: AABB[], x1: number, z1: number, x2: number, z2: number) {
  const cx = (x1 + x2) / 2;
  const cz = (z1 + z2) / 2;
  const w = x2 - x1;
  const d = z2 - z1;
  // raised coping rim (a thin frame of tiled boxes around the lip)
  const rimH = 0.18;
  const rimW = 0.18;
  const rim = (bx: number, bz: number, bw: number, bd: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(bw, rimH, bd), MAT.tile);
    m.position.set(bx, rimH / 2, bz);
    root.add(m);
  };
  rim(cx, z1 + rimW / 2, w, rimW); // north lip
  rim(cx, z2 - rimW / 2, w, rimW); // south lip
  rim(x1 + rimW / 2, cz, rimW, d); // west lip
  rim(x2 - rimW / 2, cz, rimW, d); // east lip
  // dark recessed basin walls (so the pool reads as a hole, not a tile patch)
  const basin = new THREE.Mesh(
    new THREE.BoxGeometry(w - 2 * rimW, 0.6, d - 2 * rimW),
    new THREE.MeshStandardMaterial({ color: 0x0a0f12, roughness: 1 }),
  );
  basin.position.set(cx, -0.3, cz);
  root.add(basin);
  // water surface, a little below the rim
  const water = new THREE.Mesh(new THREE.PlaneGeometry(w - 2 * rimW, d - 2 * rimW), PROP.plunge);
  water.rotation.x = -Math.PI / 2;
  water.position.set(cx, 0.04, cz);
  root.add(water);
  colliders.push({ minX: x1, maxX: x2, minZ: z1, maxZ: z2 });
}

/** Cheap volumetric-ish steam: a few faint translucent billboards near the
 * ceiling and edges — enough to read as "steam" without whiting out the room
 * (so the thing in the fog stays visible). */
function buildSteam(root: Group, cx: number, cz: number) {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xaab0b4,
    transparent: true,
    opacity: 0.05,
    depthWrite: false,
  });
  const spots: [number, number, number][] = [
    [cx - 1.4, 2.0, cz + 0.4],
    [cx + 1.5, 2.1, cz - 0.5],
    [cx, 2.3, cz + 0.9],
    [cx - 0.5, 1.8, cz - 1.0],
    [cx + 0.8, 1.9, cz + 1.1],
  ];
  for (const [x, y, z] of spots) {
    const p = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 1.9), mat);
    p.position.set(x, y, z);
    root.add(p);
  }
}

// ── Decorative props ──────────────────────────────────────────────────────────
// Everything below is PURELY VISUAL: no colliders, no seats. Props hug walls,
// corners, furniture, or the ceiling so they never block movement or the camera.
function buildProps(root: Group, lights: Light[]) {
  ruleSign(root);
  floorDrain(root);
  saunaRocks(root);
  steamGenerator(root);
  bucketAndLadle(root, 1.1, 0.6); // beside the dry stove
  bucketAndLadle(root, 8.7, 0.6); // beside the steam generator
  receptionDetails(root, lights);
  exitSignLight(root);
  doorFrame(root, 1, 2, 4); // dry sauna door gap
  doorFrame(root, 7, 8, 4); // steam sauna door gap
  doorFrame(root, 4.5, 5.5, 10); // exit gap
  archFrame(root, 4, 6, 7); // rest → reception archway
  towelHooks(root);
  wallThermometer(root);
  baseboardsAndFixtures(root, lights);
}

/** A small board with the bathing routine, drawn on a CanvasTexture, framed in
 * dark wood and mounted on the rest-area face of the sauna divider wall. */
function ruleSign(root: Group) {
  const g = new THREE.Group();
  g.position.set(5.0, 1.7, 4 + T / 2 + 0.01); // proud of the z=4 wall, facing +z
  const frame = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.4, 0.03), PROP.darkWood);
  g.add(frame);
  const boardMat = new THREE.MeshStandardMaterial({
    map: signTex(["BATHING ROUTINE", "15 DRY · 5 COOL", "15 STEAM · 5 COOL", "THEN LEAVE"]),
    roughness: 0.95,
  });
  const board = new THREE.Mesh(new THREE.PlaneGeometry(0.44, 0.34), boardMat);
  board.position.z = 0.018;
  g.add(board);
  root.add(g);
}

/** Recessed dark grate in the rest-area floor — home of the drain voice. */
function floorDrain(root: Group) {
  const g = new THREE.Group();
  g.position.set(3.0, 0.0, 5.6);
  const rim = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.04, 0.4), PROP.grate);
  rim.position.y = 0.01;
  g.add(rim);
  const basin = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.05, 0.34), new THREE.MeshStandardMaterial({ color: 0x070707, roughness: 1 }));
  basin.position.y = 0.005;
  g.add(basin);
  for (let i = 0; i < 6; i++) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.02, 0.025), PROP.grate);
    bar.position.set(0, 0.03, -0.14 + i * 0.056);
    g.add(bar);
  }
  root.add(g);
}

/** Cluster of löyly rocks heaped on top of the dry stove (NE corner, top y≈0.7). */
function saunaRocks(root: Group) {
  const g = new THREE.Group();
  g.position.set(0.5, 0.72, 0.5);
  const spots: [number, number, number, boolean][] = [
    [-0.12, 0.0, -0.1, false],
    [0.1, 0.0, -0.08, true],
    [0.0, 0.02, 0.08, false],
    [-0.08, 0.04, 0.06, true],
    [0.12, 0.03, 0.1, false],
    [0.0, 0.08, -0.02, true],
  ];
  for (const [x, y, z, hot] of spots) {
    const r = 0.05 + Math.random() * 0.03;
    const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), hot ? PROP.rockHot : PROP.rock);
    rock.position.set(x, y, z);
    rock.rotation.set(Math.random(), Math.random(), Math.random());
    g.add(rock);
  }
  root.add(g);
}

/** Steam room has no apparatus — give it a metal generator box with a cool vent. */
function steamGenerator(root: Group) {
  const g = new THREE.Group();
  g.position.set(9.3, 0.25, 0.5); // steam-sauna NE corner
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.5, 0.4), MAT.metal);
  g.add(box);
  const vent = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.16, 0.02), PROP.ventCool);
  vent.position.set(0, 0.05, 0.205);
  g.add(vent);
  const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.6, 8), MAT.metal);
  pipe.position.set(0, 0.45, -0.15);
  g.add(pipe);
  root.add(g);
}

/** Wooden pail + a thin ladle on the floor at (x,z). */
function bucketAndLadle(root: Group, x: number, z: number) {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  const pail = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.09, 0.22, 14), PROP.pail);
  pail.position.y = 0.11;
  g.add(pail);
  const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.115, 0.115, 0.03, 14), MAT.metal);
  rim.position.y = 0.2;
  g.add(rim);
  const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.34, 8), PROP.pail);
  handle.position.set(0.13, 0.18, 0.02);
  handle.rotation.z = 0.5;
  g.add(handle);
  const scoop = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.035, 0.04, 12), MAT.metal);
  scoop.position.set(0.21, 0.03, 0.02);
  g.add(scoop);
  root.add(g);
}

/** Upper plank + backrest against a sauna's south wall (z=zWall), for the bench
 * whose lower seat run sits just in front of it. Bathers face -z (away). */
function tieredBench(root: Group, x1: number, x2: number, zWall: number) {
  const cx = (x1 + x2) / 2;
  const w = x2 - x1;
  const upper = new THREE.Mesh(new THREE.BoxGeometry(w, 0.08, 0.3), MAT.woodHot);
  upper.position.set(cx, 0.85, zWall - 0.22);
  root.add(upper);
  for (const px of [x1 + 0.1, x2 - 0.1]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.85, 0.07), MAT.woodHot);
    post.position.set(px, 0.42, zWall - 0.22);
    root.add(post);
  }
  const back = new THREE.Mesh(new THREE.BoxGeometry(w, 0.16, 0.04), MAT.woodHot);
  back.position.set(cx, 0.66, zWall - 0.07);
  root.add(back);
}

/** Key-hook board (no keys), a stool behind the desk, a warm desk lamp, and a
 * round wall clock high on the reception wall. */
function receptionDetails(root: Group, lights: Light[]) {
  // (a) key-hook board on the west reception wall
  const board = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.22, 0.34), PROP.darkWood);
  board.position.set(T / 2 + 0.04, 1.6, 8.6);
  root.add(board);
  for (let i = 0; i < 5; i++) {
    const hook = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.05, 6), MAT.metal);
    hook.rotation.z = Math.PI / 2;
    hook.position.set(T / 2 + 0.08, 1.6, 8.47 + i * 0.065);
    root.add(hook);
  }

  // (b) simple stool behind the desk (desk spans x0.4..3.0 z7.4..8.0)
  const seat = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.05, 16), PROP.darkWood);
  seat.position.set(1.7, 0.5, 8.4);
  root.add(seat);
  for (const [dx, dz] of [[-0.1, -0.1], [0.1, -0.1], [-0.1, 0.1], [0.1, 0.1]] as const) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.48, 6), PROP.darkWood);
    leg.position.set(1.7 + dx, 0.24, 8.4 + dz);
    root.add(leg);
  }

  // (c) warm desk lamp on the reception desk (desk top y=1.05)
  const lampG = new THREE.Group();
  lampG.position.set(0.9, 1.05, 7.7);
  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.04, 14), MAT.metal);
  lampG.add(base);
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.22, 8), MAT.metal);
  stem.position.y = 0.13;
  lampG.add(stem);
  const shade = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.1, 0.1, 16, 1, true), PROP.lampWarm);
  shade.position.y = 0.27;
  lampG.add(shade);
  root.add(lampG);
  const deskGlow = new THREE.PointLight(0xffc070, 0.5, 1.8, 2);
  deskGlow.position.set(0.9, 1.28, 7.7);
  root.add(deskGlow);
  lights.push(deskGlow);

  // (d) round wall clock high on the south reception wall (z=10)
  const clockG = new THREE.Group();
  clockG.position.set(7.5, 2.1, 10 - T / 2 - 0.02); // faces -z into reception
  clockG.rotation.x = Math.PI / 2;
  const face = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.04, 24), PROP.cloth);
  clockG.add(face);
  const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.03, 24, 1, true), PROP.darkWood);
  clockG.add(ring);
  root.add(clockG);
  const hourHand = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.09, 0.01), PROP.darkWood);
  hourHand.position.set(7.5, 2.14, 10 - T / 2 - 0.05);
  root.add(hourHand);
  const minHand = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.13, 0.01), PROP.darkWood);
  minHand.position.set(7.48, 2.07, 10 - T / 2 - 0.05);
  minHand.rotation.z = Math.PI / 3;
  root.add(minHand);
}

/** Glowing EXIT light above the exit door, facing -z into reception. */
function exitSignLight(root: Group) {
  const g = new THREE.Group();
  g.position.set(5.0, 2.3, 9.95);
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.14, 0.05), PROP.grate);
  g.add(box);
  const face = new THREE.Mesh(new THREE.PlaneGeometry(0.32, 0.1), PROP.exitSign);
  face.position.z = -0.026;
  face.rotation.y = Math.PI;
  g.add(face);
  root.add(g);
}

/** Two vertical jamb posts + a lintel framing a door gap [x1..x2] at wall z. */
function doorFrame(root: Group, x1: number, x2: number, z: number) {
  const postH = 2.15;
  const depth = T + 0.05;
  for (const px of [x1, x2]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.06, postH, depth), PROP.darkWood);
    post.position.set(px, postH / 2, z);
    root.add(post);
  }
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(x2 - x1 + 0.06, 0.1, depth), PROP.darkWood);
  lintel.position.set((x1 + x2) / 2, postH + 0.05, z);
  root.add(lintel);
}

/** Wider open archway (no leaf) between the rest area and reception. */
function archFrame(root: Group, x1: number, x2: number, z: number) {
  const postH = 2.25;
  const depth = T + 0.06;
  for (const px of [x1, x2]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.1, postH, depth), PROP.darkWood);
    post.position.set(px, postH / 2, z);
    root.add(post);
  }
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(x2 - x1 + 0.1, 0.18, depth), PROP.darkWood);
  lintel.position.set((x1 + x2) / 2, postH + 0.09, z);
  root.add(lintel);
}

/** A couple of hooks on the rest-area west wall with one draped towel, past the showers. */
function towelHooks(root: Group) {
  const wx = T / 2 + 0.02;
  for (const z of [6.6, 6.85]) {
    const hook = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.06, 6), MAT.metal);
    hook.rotation.z = Math.PI / 2;
    hook.position.set(wx + 0.02, 1.6, z);
    root.add(hook);
  }
  const towel = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.55, 0.26), PROP.cloth);
  towel.position.set(wx + 0.03, 1.32, 6.7);
  root.add(towel);
}

/** Small round dial thermometer on the dry-sauna west wall. */
function wallThermometer(root: Group) {
  const g = new THREE.Group();
  g.position.set(T / 2 + 0.03, 1.6, 2.2);
  g.rotation.z = Math.PI / 2;
  const dial = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.03, 20), PROP.cloth);
  g.add(dial);
  const bezel = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.025, 20, 1, true), MAT.metal);
  g.add(bezel);
  root.add(g);
  const needle = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.07, 0.008), new THREE.MeshStandardMaterial({ color: 0xb02414 }));
  needle.position.set(T / 2 + 0.06, 1.62, 2.2);
  needle.rotation.x = Math.PI / 4;
  root.add(needle);
}

/** Thin dark trim at the base of the outer perimeter walls + visible lamp
 * housings on the ceiling under each room lamp so the lights read as fixtures. */
function baseboardsAndFixtures(root: Group, _lights: Light[]) {
  const baseH = 0.12;
  const off = T / 2 + 0.005;
  const runs: [number, number, number, number][] = [
    [0.1, off, FP - 0.1, off], // north
    [0.1, FP - off, FP - 0.1, FP - off], // south
    [off, 0.1, off, FP - 0.1], // west
    [FP - off, 0.1, FP - off, FP - 0.1], // east
  ];
  for (const [x1, z1, x2, z2] of runs) {
    const horizontal = z1 === z2;
    const w = horizontal ? Math.abs(x2 - x1) : 0.04;
    const d = horizontal ? 0.04 : Math.abs(z2 - z1);
    const strip = new THREE.Mesh(new THREE.BoxGeometry(w, baseH, d), PROP.darkWood);
    strip.position.set((x1 + x2) / 2, baseH / 2, (z1 + z2) / 2);
    root.add(strip);
  }
  const fixtures: [number, number][] = [
    [2.5, 1.8], // dry sauna
    [7.5, 1.8], // steam
    [3.0, 5.5], // rest west
    [7.0, 5.5], // rest east
    [3.0, 8.5], // reception west
    [7.0, 8.5], // reception east
  ];
  for (const [x, z] of fixtures) {
    const housing = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.08, 0.3), PROP.housing);
    housing.position.set(x, H - 0.04, z);
    root.add(housing);
    const lens = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 0.22), PROP.lampWarm);
    lens.rotation.x = Math.PI / 2;
    lens.position.set(x, H - 0.085, z);
    root.add(lens);
  }
}
